/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPost, fetchDelete } from 'FetchAPIs';
import { stripDevicesFromPath, stripSystemChunkFromLocationHierarchyPath } from 'hierarchyUtils';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorFrequencies: 'Error loading time frequencies. {message}',
  errorLoadingGranularities: 'Error loading granularities. {message}',
  errorAddingGranularity: 'Error adding granularity. {message}',
  errorRemovingGranularity: 'Error removing granularity. {message}',
  errorUpdatingGranularity: 'Error updating granularity. {message}',
});

export function getAvailableAggregates() {
  return fetchGet(`${baseHierarchiesURL}/workspaces/granularity/timeFrequency`).catch(error => {
    throw new I18NError(messages.errorFrequencies, {
      message: formatErrorResponse(error),
    });
  });
}

export function getGranularity(hierarchyPath, granularity) {
  return fetchGet(
    `${baseHierarchiesURL}${stripDevicesFromPath(
      stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
    )}/granularity/${granularity}`
  ).catch(error => {
    throw new I18NError(messages.errorLoadingGranularities, {
      message: formatErrorResponse(error),
    });
  });
}

export function getGranularities(hierarchyPath) {
  return fetchGet(
    `${baseHierarchiesURL}${stripDevicesFromPath(
      stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
    )}/granularity`
  ).catch(error => {
    throw new I18NError(messages.errorLoadingGranularities, {
      message: formatErrorResponse(error),
    });
  });
}

export function addGranularity(hierarchyPath, grainData, userEmailId) {
  return fetchPost(
    `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(hierarchyPath)}/granularity`,
    grainData,
    { userEmailId }
  )
    .then(() => getGranularities(hierarchyPath))
    .catch(error => {
      throw new I18NError(messages.errorAddingGranularity, {
        message: formatErrorResponse(error),
      });
    });
}

export function removeGranularity(hierarchyPath, id, userEmailId) {
  return fetchDelete(
    `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(
      hierarchyPath
    )}/granularity/${id}`,
    null,
    {
      userEmailId,
    }
  )
    .then(() => getGranularities(hierarchyPath))
    .catch(error => {
      throw new I18NError(messages.errorRemovingGranularity, {
        message: formatErrorResponse(error),
      });
    });
}

// PUT /granularity no longer exists for v2 hierarchy-based APIs, we need to delete and then recreate
export function updateGranularity(hierarchyPath, id, grainData, userEmailId) {
  return fetchDelete(
    `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(
      hierarchyPath
    )}/granularity/${id}`,
    null,
    {
      userEmailId,
    }
  )
    .then(() =>
      fetchPost(
        `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(
          hierarchyPath
        )}/granularity`,
        grainData,
        { userEmailId }
      )
    )
    .then(() => getGranularities(hierarchyPath))
    .catch(error => {
      throw new I18NError(messages.errorUpdatingGranularity, {
        message: formatErrorResponse(error),
      });
    });
}
